/**
 * @fileoverview Phone Number Module Type Definitions
 * 
 * This file contains all type definitions specific to the Phone Number Module,
 * including configuration interfaces, search filters, and purchase workflows.
 */

/**
 * @typedef {Object} PhoneNumberConfig
 * @property {'onboarding'|'dashboard'|'admin'} context - Context where the module is used
 * @property {SearchFilter[]} searchFilters - Available search filters for this context
 * @property {'immediate'|'confirmation'|'admin-approval'} purchaseFlow - Purchase workflow type
 * @property {function(PhoneNumber): void} [onNumberSelected] - Callback when number is selected
 * @property {function(PurchaseResult): void} [onPurchaseComplete] - Callback when purchase completes
 * @property {boolean} [showSkipOption] - Whether to show skip option (for onboarding)
 * @property {BillingContext} [billingContext] - Billing context for purchase validation
 * @property {SecurityLevel} [securityLevel] - Security level for operations
 * @property {boolean} [allowBatchPurchase] - Allow purchasing multiple numbers (admin only)
 * @property {number} [maxResults] - Maximum search results to display
 * @property {boolean} [enableAdvancedSearch] - Enable advanced search features
 */

/**
 * @typedef {Object} SearchFilter
 * @property {'country'|'area-code'|'city'|'region'|'pattern'|'postal-code'|'exchange'} type - Filter type
 * @property {boolean} enabled - Whether this filter is enabled
 * @property {boolean} [required] - Whether this filter is required
 * @property {*} [defaultValue] - Default value for the filter
 * @property {string} [label] - Display label for the filter
 * @property {string} [placeholder] - Placeholder text for input
 * @property {FilterOption[]} [options] - Available options for select filters
 * @property {ValidationRule[]} [validation] - Validation rules for the filter
 */

/**
 * @typedef {Object} FilterOption
 * @property {string} value - Option value
 * @property {string} label - Option display label
 * @property {boolean} [disabled] - Whether option is disabled
 * @property {Object} [metadata] - Additional option metadata
 */

/**
 * @typedef {Object} ValidationRule
 * @property {'required'|'minLength'|'maxLength'|'pattern'|'custom'} type - Validation type
 * @property {*} value - Validation value (e.g., min length, regex pattern)
 * @property {string} message - Error message for validation failure
 * @property {function(*): boolean} [validator] - Custom validation function
 */

/**
 * @typedef {Object} PhoneNumber
 * @property {string} phoneNumber - The phone number in E.164 format
 * @property {string} friendlyName - Human-readable formatted number
 * @property {string} [locality] - City or locality
 * @property {string} [region] - State or region
 * @property {string} [country] - Country code
 * @property {string} [postalCode] - Postal/ZIP code
 * @property {PhoneCapabilities} [capabilities] - Number capabilities
 * @property {PhonePricing} [pricing] - Pricing information
 * @property {Object} [metadata] - Additional number metadata
 */

/**
 * @typedef {Object} PhoneCapabilities
 * @property {boolean} voice - Supports voice calls
 * @property {boolean} sms - Supports SMS messages
 * @property {boolean} mms - Supports MMS messages
 * @property {boolean} fax - Supports fax
 * @property {string[]} [features] - Additional features supported
 */

/**
 * @typedef {Object} PhonePricing
 * @property {PriceInfo} setup - One-time setup cost
 * @property {PriceInfo} monthly - Monthly recurring cost
 * @property {PriceInfo} [usage] - Usage-based pricing
 * @property {string} currency - Currency code (e.g., 'USD')
 * @property {Object} [discounts] - Available discounts
 */

/**
 * @typedef {Object} PriceInfo
 * @property {number} amount - Price amount in cents
 * @property {boolean} isIncluded - Whether cost is included in plan
 * @property {string} [description] - Price description
 */

/**
 * @typedef {Object} SearchCriteria
 * @property {string} [countryCode] - Country code filter
 * @property {string} [areaCode] - Area code filter
 * @property {string} [city] - City filter
 * @property {string} [region] - State/region filter
 * @property {string} [postalCode] - Postal code filter
 * @property {string} [phoneExchange] - Phone exchange filter
 * @property {string} [letterPattern] - Letter pattern search
 * @property {string} [numberPattern] - Number pattern search
 * @property {string} [contains] - General contains filter
 * @property {number} [limit] - Maximum results to return
 */

/**
 * @typedef {Object} SearchResult
 * @property {PhoneNumber[]} results - Array of available phone numbers
 * @property {number} total - Total number of results available
 * @property {boolean} hasMore - Whether more results are available
 * @property {SearchMetadata} [metadata] - Search metadata
 */

/**
 * @typedef {Object} SearchMetadata
 * @property {number} searchTime - Time taken for search in milliseconds
 * @property {string} [searchId] - Unique search identifier
 * @property {Object} [filters] - Applied filters
 * @property {string} [provider] - Search provider used
 */

/**
 * @typedef {Object} PurchaseRequest
 * @property {string} phoneNumber - Phone number to purchase
 * @property {string} [userId] - User ID for the purchase (admin context)
 * @property {Object} [billingInfo] - Billing information
 * @property {Object} [metadata] - Purchase metadata
 */

/**
 * @typedef {Object} PurchaseResult
 * @property {boolean} success - Whether purchase was successful
 * @property {PhoneNumber} phoneNumber - Purchased phone number details
 * @property {string} [transactionId] - Transaction identifier
 * @property {PurchaseReceipt} [receipt] - Purchase receipt
 * @property {string} [error] - Error message if purchase failed
 * @property {Object} [metadata] - Additional result metadata
 */

/**
 * @typedef {Object} PurchaseReceipt
 * @property {string} id - Receipt ID
 * @property {Date} purchaseDate - Purchase timestamp
 * @property {PriceInfo} totalCost - Total cost paid
 * @property {PriceInfo} setupFee - Setup fee paid
 * @property {PriceInfo} monthlyFee - Monthly fee amount
 * @property {string} paymentMethod - Payment method used
 * @property {Object} [billingAddress] - Billing address
 */

/**
 * @typedef {Object} BillingContext
 * @property {string} [subscriptionId] - Current subscription ID
 * @property {string} [planId] - Current plan ID
 * @property {number} [phoneNumbersIncluded] - Numbers included in plan
 * @property {number} [phoneNumbersUsed] - Numbers currently used
 * @property {boolean} [canPurchaseMore] - Whether user can purchase more numbers
 * @property {PriceInfo} [additionalNumberCost] - Cost for additional numbers
 */

/**
 * @typedef {'user'|'admin'} SecurityLevel
 */

/**
 * Phone number search types
 * @readonly
 * @enum {string}
 */
export const SearchType = {
  BASIC: 'basic',
  ADVANCED: 'advanced',
  PATTERN: 'pattern'
};

/**
 * Purchase flow types
 * @readonly
 * @enum {string}
 */
export const PurchaseFlow = {
  IMMEDIATE: 'immediate',
  CONFIRMATION: 'confirmation',
  ADMIN_APPROVAL: 'admin-approval'
};

/**
 * Module context types for phone number module
 * @readonly
 * @enum {string}
 */
export const PhoneNumberContext = {
  ONBOARDING: 'onboarding',
  DASHBOARD: 'dashboard',
  ADMIN: 'admin'
};

/**
 * Phone number module event types
 * @readonly
 * @enum {string}
 */
export const PhoneNumberEventType = {
  SEARCH_STARTED: 'search-started',
  SEARCH_COMPLETED: 'search-completed',
  SEARCH_FAILED: 'search-failed',
  NUMBER_SELECTED: 'number-selected',
  PURCHASE_STARTED: 'purchase-started',
  PURCHASE_COMPLETED: 'purchase-completed',
  PURCHASE_FAILED: 'purchase-failed',
  PURCHASE_CANCELLED: 'purchase-cancelled',
  AVAILABILITY_CHECKED: 'availability-checked',
  AVAILABILITY_CHECK_FAILED: 'availability-check-failed'
};

/**
 * Default search filters for different contexts
 */
export const DEFAULT_SEARCH_FILTERS = {
  [PhoneNumberContext.ONBOARDING]: [
    { type: 'country', enabled: true, required: false, defaultValue: 'US' },
    { type: 'area-code', enabled: true, required: false },
    { type: 'city', enabled: true, required: false },
    { type: 'region', enabled: true, required: false }
  ],
  [PhoneNumberContext.DASHBOARD]: [
    { type: 'country', enabled: true, required: false, defaultValue: 'US' },
    { type: 'area-code', enabled: true, required: false },
    { type: 'city', enabled: true, required: false },
    { type: 'region', enabled: true, required: false },
    { type: 'pattern', enabled: true, required: false },
    { type: 'postal-code', enabled: true, required: false }
  ],
  [PhoneNumberContext.ADMIN]: [
    { type: 'country', enabled: true, required: false, defaultValue: 'US' },
    { type: 'area-code', enabled: true, required: false },
    { type: 'city', enabled: true, required: false },
    { type: 'region', enabled: true, required: false },
    { type: 'pattern', enabled: true, required: false },
    { type: 'postal-code', enabled: true, required: false },
    { type: 'exchange', enabled: true, required: false }
  ]
};

/**
 * Default phone number module configuration
 */
export const DEFAULT_PHONE_NUMBER_CONFIG = {
  context: PhoneNumberContext.DASHBOARD,
  searchFilters: DEFAULT_SEARCH_FILTERS[PhoneNumberContext.DASHBOARD],
  purchaseFlow: PurchaseFlow.CONFIRMATION,
  showSkipOption: false,
  securityLevel: 'user',
  allowBatchPurchase: false,
  maxResults: 20,
  enableAdvancedSearch: true
};
